import clsx from 'clsx'
import {FC} from 'react'
import {CSSObjectWithLabel} from 'react-select'
import './BusinessOption.scss'

/** What a business picker shows for one business (server: BusinessSwitcherLabel, backend 1.0.0.638+). */
export type BusinessOptionData = {
  id?: number
  /** The CRM trade name, otherwise the official name. */
  name: string
  /** The label the business's users gave it in the panel. */
  label?: string | null
  /** 1 حقیقی، 2 حقوقی، 3 مشارکت مدنی، 4 اتباع غیرایرانی; absent when unknown or on an older backend. */
  type?: number | null
  /** The economic code alone; null when the business has none, undefined when not known. */
  economicCode?: string | null
  /** The economic code, or the national code when there is none. */
  code?: string | null
  /**
   * The tax branch code, four digits (backend 1.0.0.688+); null or absent when the business has none.
   * The branches of one taxpayer share the name and the economic code, so this tells them apart.
   */
  branchCode?: string | null
}

type TaxpayerTypeInfo = {title: string; icon: string; tone: string}

export const TAXPAYER_TYPE_INFO: Record<number, TaxpayerTypeInfo> = {
  1: {title: 'حقیقی', icon: 'la-user', tone: 'person'},
  2: {title: 'حقوقی', icon: 'la-building', tone: 'legal'},
  3: {title: 'مشارکت مدنی', icon: 'la-user-friends', tone: 'partnership'},
  4: {title: 'اتباع غیرایرانی', icon: 'la-passport', tone: 'foreign'},
}

const clean = (value?: string | null) => (value || '').trim()

/** The identifying code and what it is: the economic code, else the national code. */
export const businessCodeOf = (business: BusinessOptionData) => {
  const economicCode = clean(business.economicCode)
  if (economicCode) return {caption: 'کد اقتصادی', value: economicCode}
  const code = clean(business.code)
  if (!code) return undefined
  // A backend before 1.0.0.638 sends only `code`: the economic code when there is one, so it is not
  // known which code it is. A null economic code says the business has none.
  if (business.economicCode === undefined) return {caption: 'کد', value: code}
  // A foreign national's business is known by its comprehensive code («کد فراگیر»).
  return {caption: business.type === 4 ? 'کد فراگیر' : 'شناسهٔ ملی', value: code}
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

/** Folds what people type interchangeably (Arabic ي/ك, Persian and Arabic digits, ZWNJ, case) for the search. */
export const foldBusinessSearch = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/ي|ى/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[‌‍]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()

/**
 * The words a branch is searched by: «شعبه», the code as shown and without its leading zeros, so
 * «نیرو ۳» and «شعبه 0003» both find branch 0003.
 */
const branchSearchText = (branchCode?: string | null) => {
  const code = clean(branchCode)
  if (!code) return ''
  const unpadded = /^\d+$/.test(code) ? String(Number(code)) : ''
  return ['شعبه', code, unpadded].filter(Boolean).join(' ')
}

/** True when every word typed is found in the name, the label, the type, a code or the branch. */
export const businessMatchesSearch = (business: BusinessOptionData, search: string) => {
  const words = foldBusinessSearch(search).split(' ').filter(Boolean)
  if (words.length === 0) return true
  const haystack = foldBusinessSearch(
    [
      business.name,
      business.label,
      business.economicCode,
      business.code,
      branchSearchText(business.branchCode),
      business.type ? TAXPAYER_TYPE_INFO[business.type]?.title : '',
      business.id ? String(business.id) : '',
    ]
      .filter(Boolean)
      .join(' ')
  )
  return words.every((word) => haystack.includes(word))
}

/**
 * react-select's option style for a business: a light highlight instead of the solid blue, so the
 * coloured chips stay readable, and a mark on the chosen one.
 */
export const businessOptionStyle = (
  base: CSSObjectWithLabel,
  state: {isSelected: boolean; isFocused: boolean}
): CSSObjectWithLabel => ({
  ...base,
  padding: '8px 12px',
  color: 'inherit',
  backgroundColor: state.isSelected
    ? 'var(--bs-primary-light, #eef6ff)'
    : state.isFocused
    ? 'var(--bs-gray-100, #f5f8fa)'
    : 'transparent',
  boxShadow: state.isSelected ? 'inset -3px 0 0 var(--bs-primary, #3e97ff)' : undefined,
  cursor: 'pointer',
})

type Props = {
  business: BusinessOptionData
  /** One line for the chosen value of a combo: name, label and code. */
  compact?: boolean
  /** The business id, for administrators who search by it. */
  showId?: boolean
  className?: string
}

/**
 * One business in a picker: the name and its panel label on the first line; the taxpayer type
 * (a colour per type), the economic code, the tax branch and, for administrators, the id on the second.
 */
const BusinessOption: FC<Props> = ({business, compact = false, showId = false, className}) => {
  const name = clean(business.name) || (business.id ? `کسب‌وکار ${business.id}` : 'کسب‌وکار')
  const label = clean(business.label)
  const code = businessCodeOf(business)
  const branch = clean(business.branchCode)
  const type = business.type ? TAXPAYER_TYPE_INFO[business.type] : undefined

  if (compact) {
    return (
      <span className={clsx('bopt bopt--compact', className)}>
        <span className='bopt__name'>{name}</span>
        {label && <span className='bopt__label'>{label}</span>}
        {branch && (
          <span className='bopt__branch'>
            شعبه <bdi dir='ltr'>{branch}</bdi>
          </span>
        )}
        {code && (
          <bdi className='bopt__code-value' dir='ltr'>
            {code.value}
          </bdi>
        )}
      </span>
    )
  }

  return (
    <span className={clsx('bopt', className)}>
      <span className='bopt__row'>
        <span className='bopt__name'>{name}</span>
        {label && (
          <span className='bopt__label'>
            <i className='las la-tag' aria-hidden='true' />
            {label}
          </span>
        )}
      </span>
      {(type || code || branch || (showId && business.id)) && (
        <span className='bopt__row bopt__row--meta'>
          {type && (
            <span className={clsx('bopt__type', `bopt__type--${type.tone}`)}>
              <i className={clsx('las', type.icon)} aria-hidden='true' />
              {type.title}
            </span>
          )}
          {code && (
            <span className='bopt__code'>
              <span className='bopt__code-caption'>{code.caption}</span>
              <bdi className='bopt__code-value' dir='ltr'>
                {code.value}
              </bdi>
            </span>
          )}
          {branch && (
            <span className='bopt__code'>
              <span className='bopt__code-caption'>شعبه</span>
              <bdi className='bopt__code-value' dir='ltr'>
                {branch}
              </bdi>
            </span>
          )}
          {showId && business.id ? (
            <span className='bopt__id' title='شناسهٔ کسب‌وکار در پنل'>
              #{business.id}
            </span>
          ) : null}
        </span>
      )}
    </span>
  )
}

export {BusinessOption}
